import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useRoomContext,
  useParticipants,
  useLocalParticipant,
} from '@livekit/components-react';
import { RoomEvent, Track } from 'livekit-client';
import { muteLiveKitParticipant } from '../../services/liveKitService';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

const ACTION_LABELS = {
  ask_unmute: 'asked you to unmute your microphone',
  ask_mute: 'asked you to mute your microphone',
  ask_camera_on: 'asked you to turn on your camera',
  ask_camera_off: 'asked you to turn off your camera',
};

/**
 * Extra WorkLoop call features: host mute and ask mute/unmute/camera.
 * Must render inside <LiveKitRoom>.
 */
const LiveKitCallExtras = ({
  channelId,
  messageId,
  roomName,
  isOrganizer = false,
}) => {
  const room = useRoomContext();
  const participants = useParticipants();
  const { localParticipant } = useLocalParticipant();

  const [panelOpen, setPanelOpen] = useState(false);
  const [toast, setToast] = useState('');
  const [incoming, setIncoming] = useState(null);
  const [busy, setBusy] = useState(false);
  const toastTimerRef = useRef(null);

  const remoteParticipants = useMemo(
    () => (participants || []).filter((p) => !p.isLocal),
    [participants],
  );

  const showToast = useCallback((msg, durationMs = 3200) => {
    setToast(msg);
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(''), durationMs);
  }, []);

  const publishControl = useCallback(async (action, targetIdentity) => {
    if (!room?.localParticipant) return;
    const payload = {
      type: 'wl_call_control',
      action,
      targetIdentity: targetIdentity || null,
      fromIdentity: room.localParticipant.identity,
      fromName: room.localParticipant.name || room.localParticipant.identity,
    };
    const data = encoder.encode(JSON.stringify(payload));
    await room.localParticipant.publishData(data, {
      reliable: true,
      topic: 'wl_call_control',
    });
  }, [room]);

  const handleAsk = useCallback(async (action, identity) => {
    try {
      await publishControl(action, identity);
      showToast('Request sent');
    } catch (err) {
      showToast(err?.message || 'Failed to send request');
    }
  }, [publishControl, showToast]);

  const handleHostMute = useCallback(async (identity, kind, muted) => {
    setBusy(true);
    try {
      await muteLiveKitParticipant({
        channelId,
        messageId,
        roomName,
        identity,
        muted,
        kind,
      });
      showToast(muted ? `Muted ${kind}` : `Unmuted ${kind}`);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Mute failed';
      showToast(typeof msg === 'string' ? msg : 'Mute failed');
    } finally {
      setBusy(false);
    }
  }, [channelId, messageId, roomName, showToast]);

  // Incoming control requests
  useEffect(() => {
    if (!room) return undefined;

    const onData = (payload, participant, _kind, topic) => {
      if (topic && topic !== 'wl_call_control') return;
      try {
        const msg = JSON.parse(decoder.decode(payload));
        if (msg?.type !== 'wl_call_control') return;
        const action = msg.action;
        if (!ACTION_LABELS[action]) return;
        // If targeted, only the target should see it
        if (msg.targetIdentity && msg.targetIdentity !== room.localParticipant?.identity) {
          return;
        }
        setIncoming({
          action,
          fromName: msg.fromName || participant?.name || 'Host',
        });
      } catch {
        // ignore
      }
    };

    room.on(RoomEvent.DataReceived, onData);
    return () => {
      room.off(RoomEvent.DataReceived, onData);
    };
  }, [room]);

  const acceptIncoming = useCallback(async () => {
    if (!incoming || !localParticipant) {
      setIncoming(null);
      return;
    }
    try {
      if (incoming.action === 'ask_unmute') {
        await localParticipant.setMicrophoneEnabled(true);
      } else if (incoming.action === 'ask_mute') {
        await localParticipant.setMicrophoneEnabled(false);
      } else if (incoming.action === 'ask_camera_on') {
        await localParticipant.setCameraEnabled(true);
      } else if (incoming.action === 'ask_camera_off') {
        await localParticipant.setCameraEnabled(false);
      }
    } catch (err) {
      showToast(err?.message || 'Could not update device');
    }
    setIncoming(null);
  }, [incoming, localParticipant, showToast]);

  useEffect(() => () => {
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
  }, []);


  return (
    <>
      <div className="absolute top-3 right-3 z-30 flex flex-col items-end gap-2">
        <div className="flex flex-wrap justify-end gap-1.5">
          <button
            type="button"
            onClick={() => setPanelOpen((v) => !v)}
            className="px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-white/15 hover:bg-white/25 text-white"
          >
            {panelOpen ? 'Hide controls' : 'Call controls'}
          </button>
        </div>

        {panelOpen && (
          <div className="w-80 max-h-[70vh] overflow-y-auto rounded-xl border border-white/15 bg-black/85 text-white shadow-xl p-3 space-y-3">
            <p className="text-xs text-white/60">
              Ask participants to mute/unmute or use camera.
              {isOrganizer ? ' As organizer you can also force-mute.' : ''}
            </p>

            {remoteParticipants.length === 0 && (
              <p className="text-xs text-white/50">No other participants yet.</p>
            )}

            {remoteParticipants.map((p) => {
              const name = p.name || p.identity;
              const audioPub = typeof p.getTrackPublication === 'function'
                ? p.getTrackPublication(Track.Source.Microphone)
                : null;
              const videoPub = typeof p.getTrackPublication === 'function'
                ? p.getTrackPublication(Track.Source.Camera)
                : null;
              const micOn = Boolean(audioPub && !audioPub.isMuted);
              const camOn = Boolean(videoPub && !videoPub.isMuted);

              return (
                <div key={p.identity} className="rounded-lg bg-white/5 p-2.5 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium truncate">{name}</p>
                    <p className="text-[10px] text-white/50 shrink-0">
                      {micOn ? 'Mic on' : 'Mic off'} · {camOn ? 'Cam on' : 'Cam off'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleAsk('ask_unmute', p.identity)}
                      className="px-2 py-1 text-[11px] rounded-md bg-white/10 hover:bg-white/20"
                    >
                      Ask unmute
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleAsk('ask_mute', p.identity)}
                      className="px-2 py-1 text-[11px] rounded-md bg-white/10 hover:bg-white/20"
                    >
                      Ask mute
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleAsk('ask_camera_on', p.identity)}
                      className="px-2 py-1 text-[11px] rounded-md bg-white/10 hover:bg-white/20"
                    >
                      Ask camera
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleAsk('ask_camera_off', p.identity)}
                      className="px-2 py-1 text-[11px] rounded-md bg-white/10 hover:bg-white/20"
                    >
                      Ask cam off
                    </button>
                    {isOrganizer && (
                      <>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleHostMute(p.identity, 'audio', true)}
                          className="px-2 py-1 text-[11px] rounded-md bg-amber-500/90 text-black font-semibold"
                        >
                          Force mute
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => handleHostMute(p.identity, 'video', true)}
                          className="px-2 py-1 text-[11px] rounded-md bg-amber-500/90 text-black font-semibold"
                        >
                          Force cam off
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {toast && (
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-40 px-3 py-1.5 rounded-lg bg-white text-black text-xs font-medium shadow">
          {toast}
        </div>
      )}

      {incoming && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-sm rounded-2xl bg-white text-gray-900 p-5 shadow-2xl">
            <p className="text-sm font-semibold">
              {incoming.fromName} {ACTION_LABELS[incoming.action]}
            </p>
            <div className="mt-4 flex gap-2">
              <button
                type="button"
                onClick={acceptIncoming}
                className="flex-1 px-3 py-2 rounded-xl bg-primary-600 text-white text-sm font-semibold"
              >
                Accept
              </button>
              <button
                type="button"
                onClick={() => setIncoming(null)}
                className="flex-1 px-3 py-2 rounded-xl border border-gray-300 text-sm font-semibold"
              >
                Decline
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LiveKitCallExtras;
